import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../SessionManagement/AuthContext";
import { xorEncrypt, xorDecrypt } from "../../encrypt";
import CryptoJS from "crypto-js";
import {
  Alert,
  Box,
  Container,
  TextField,
  Typography,
  Snackbar,
  Grid,
  Link,
  Avatar,
  IconButton,
  InputAdornment,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LoadingButton from "@mui/lab/LoadingButton";
import useSharedLink from "../../SharedLink";
import { useSnackbar } from "../../Providers/SnackbarProvider";
import signinImage from "../../assets/img/abanotes3.png";
import "../../css/normalize.css";
import axiosInstance from "../../api/axiosInstance";

const Login = () => {
  const sharedLink = useSharedLink();
  const navigate = useNavigate();
  const { dispatch } = useContext(AuthContext);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [alertContent, setAlertContent] = useState("");
  const [alert, setAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [redirectMessage, setRedirectMessage] = useState(false);
  const { openSnackbar } = useSnackbar();

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
  useEffect(() => {
    const clearLocalStorageIfExpired = () => {
      const lastLoginTime = localStorage.getItem("lastLoginTime");
      if (lastLoginTime) {
        const now = new Date();
        const lastLoginDate = new Date(lastLoginTime);
        const differenceInHours = (now - lastLoginDate) / 1000 / 60 / 60;

        if (differenceInHours >= 24) {
          localStorage.clear();
          navigate("/login");
        }
      }
    };

    clearLocalStorageIfExpired();
    const encryptedUsername = localStorage.getItem("username");
    const encryptedEnrollmentStatus = localStorage.getItem("enrollmentStatus");

    const savedUsername = encryptedUsername
      ? xorDecrypt(encryptedUsername)
      : null;
    const enrollmentStatus = encryptedEnrollmentStatus
      ? xorDecrypt(encryptedEnrollmentStatus)
      : null;
    //console.log(`Decrypted Enrollment Status: ${enrollmentStatus}`); // Debugging line

    document.body.style.backgroundImage = `url(${signinImage})`;
    document.body.style.backgroundSize = "cover";

    if (savedUsername) {
      setRedirectMessage(true);
      const timeoutId = setTimeout(() => {
        if (enrollmentStatus === "true") {
          //console.log("Redirecting to /dashtemp"); // Debugging line
          navigate("/dashtemp");
        } else {
          //console.log("Redirecting to /"); // Debugging line
          navigate("/");
        }
        setRedirectMessage(false);
      }, 1000);

      return () => {
        document.body.style.backgroundImage = "";
        document.body.style.backgroundSize = "";
        clearTimeout(timeoutId);
      };
    }
  }, [navigate]);

  const SECRET_KEY = CryptoJS.enc.Hex.parse(
    "4c9edd026c240f960008cdf5f775126bbe0a4154b632ffed169959274376404b"
  );

  const encryptData = (data) => {
    return CryptoJS.AES.encrypt(data, SECRET_KEY, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7, // Ensure this matches backend padding
    }).toString();
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const encryptedUsername = encryptData(username);
    const encryptedPassword = encryptData(password);
    const data = {
      username: encryptedUsername,
      password: encryptedPassword,
      encoded: "Yes",
    };

    try {
      const loginResponse = await axios.post(
        sharedLink + "/api/authenticate",
        data,
        {
          maxRedirects: 0, // Prevent axios from following redirects automatically
          validateStatus: (status) => status < 500, // Resolve only if the status code is less than 500
        }
      );
      const token = loginResponse.data.jwt;
      localStorage.setItem("ABAtoken", xorEncrypt(token));
      //console.log("Login Response:", loginResponse); // Debugging line

      if (loginResponse.status === 302) {
        // Handle redirection manually
        const redirectUrl = loginResponse.headers.location;
        const errorResponse = await axiosInstance.get(redirectUrl, {
          withCredentials: true,
        });

        //console.log("Error Response:", errorResponse); // Debugging line

        if (
          errorResponse.status === 200 &&
          typeof errorResponse.data === "string" &&
          errorResponse.data.includes("<html")
        ) {
          setAlert(true);
          setAlertContent("Invalid Credentials");
          setLoading(false);
          return;
        }
      } else if (
        loginResponse.status === 200 &&
        typeof loginResponse.data !== "string"
      ) {
        //console.log("Logged in:", loginResponse.data);
        const userDetailsResponse = await axiosInstance.post(
          sharedLink + "/api/userdetails",
          {
            username: encryptedUsername,
            password: encryptedPassword,
            encoded: "Yes",
          },
          { withCredentials: true }
        );

        //console.log("User Details Response:", userDetailsResponse); // Debugging line

        if (userDetailsResponse.status === 200) {
          //console.log("User Details:", userDetailsResponse.data);
          const userData = userDetailsResponse.data[0];

          const encryptedUsername = xorEncrypt(userData?.email);
          //console.log(`Encrypted Username: ${encryptedUsername}`);
          localStorage.setItem("username", encryptedUsername);

          const encryptedSubscriberId = xorEncrypt(
            userData?.id ? userData.id.toString() : "1"
          );
          //console.log(`Encrypted Subscriber ID: ${encryptedSubscriberId}`);
          localStorage.setItem("subscriberId", encryptedSubscriberId);
          localStorage.setItem("userRole", xorEncrypt(userData?.role)); // Set user role in localStorage
          localStorage.setItem("lastLoginTime", new Date().toISOString());

          //console.log("user data:", userData);

          const enrollments = userData?.enrollments || [];
          const latestEnrollment =
            enrollments.length > 0
              ? enrollments.reduce((latest, current) => {
                  return current.id > latest.id ? current : latest;
                }, enrollments[0])
              : null;

          const enrollmentStatus = (() => {
            if (!latestEnrollment) {
              return false;
            }
            if (latestEnrollment.enrollmentStatus === "Active") {
              return true;
            }
            if (!latestEnrollment.enrollmentEndDate) {
              return false;
            }
            const enrollmentEndDate = new Date(
              latestEnrollment.enrollmentEndDate
            );
            const now = new Date();
            const differenceInDays =
              (now - enrollmentEndDate) / (1000 * 60 * 60 * 24);

            if (differenceInDays <= 7) {
              const encryptedEnrollmentEndDate = xorEncrypt(
                enrollmentEndDate.toISOString()
              );
              localStorage.setItem(
                "enrollmentEndDate",
                encryptedEnrollmentEndDate
              );
            }

            // console.log(`Enrollment End Date: ${enrollmentEndDate}`);
            // console.log(`Current Date: ${now}`);
            // console.log(`Difference in Days: ${differenceInDays}`);
            return differenceInDays <= 7;
          })();

          const enrollmentStatusString = enrollmentStatus ? "true" : "false";
          // console.log("Enrollment Status:", enrollmentStatusString);

          const encryptedEnrollmentStatus = xorEncrypt(enrollmentStatusString);
          localStorage.setItem("enrollmentStatus", encryptedEnrollmentStatus);
          // console.log(`Encrypted Enrollment Status: ${enrollmentStatusString}`);

          const productPlan = latestEnrollment?.productPlan || null;
          // console.log ("Product Plan:", productPlan);
          if (productPlan) {
            const encryptedProductPlan = xorEncrypt(productPlan);
            localStorage.setItem("productPlan", encryptedProductPlan);
            // console.log(`Encrypted Product Plan: ${encryptedProductPlan}`);
          }

          dispatch({
            type: "LOGIN",
            payload: {
              username: userData?.email,
              enrollmentStatus: enrollmentStatusString,
            },
          });

          if (userData?.role === "Admin") {
            const encryptedEnrollmentStatus = xorEncrypt("true");
            localStorage.setItem("enrollmentStatus", encryptedEnrollmentStatus);
            navigate("/dashtemp"); // redirect to the admin dashboard
            setTimeout(() => window.location.reload(), 100);
            return;
          }

          setSuccess(true);
          setTimeout(() => {
            navigate(enrollmentStatusString === "true" ? "/dashtemp" : "/");
            setSuccess(false);
          }, 1000);
        } else {
          setAlert(true);
          setAlertContent("Invalid Credentials");
        }
      } else {
        setAlert(true);
        setAlertContent("Invalid Credentials");
      }
    } catch (error) {
      console.error("Error logging in or fetching user details:", error);
      setAlertContent(error.response?.data || "Invalid Credentials");
      setAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setRedirectMessage(false);
  };

  return (
    <Container
      sx={{
        height: "80vh",
        width: { xs: "12", md: "12", xl: "12" },
        backgroundColor: "rgba(255, 255, 255, 0.3)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backdropFilter: "blur(10px)",
        borderRadius: "10px",
        border: "1px solid rgba(255, 255, 255, 0.18)",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          height: "50vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "left",
        }}
      >
        <Typography
          className="login-message"
          variant="h4"
          gutterBottom
          sx={{ ml: 10 }}
        >
          Welcome to
        </Typography>

        <Typography
          className="login-message"
          variant="h2"
          gutterBottom
          sx={{
            ml: 10,
          }}
        >
          ABA Notes
        </Typography>
      </Box>

      <Box
        className="login-container"
        component="form"
        onSubmit={handleSubmit}
        sx={{
          mt: 4,
          mb: 4,
          p: 4,
          boxShadow: 3,
          borderRadius: 2,
          bgcolor: "background.paper",
          marginLeft: "auto",
          marginRight: "20px",
          width: "400px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Sign in
        </Typography>
        <TextField
          fullWidth
          margin="normal"
          label="Email"
          name="username"
          placeholder="Enter your email"
          required
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="email"
          sx={{
            "& .MuiInputBase-input": {
              height: "1.1876em",
              padding: "10px 14px",
            },
          }}
          InputLabelProps={{
            style: {
              top: "-6px",
            },
          }}
        />
        <TextField
          fullWidth
          margin="normal"
          type={showPassword ? "text" : "password"}
          label="Password"
          name="password"
          placeholder="Enter your password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          sx={{
            "& .MuiInputBase-input": {
              height: "1.1876em",
              padding: "10px 14px",
            },
          }}
          InputLabelProps={{
            style: {
              top: "-6px",
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={togglePasswordVisibility} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <LoadingButton
          variant="contained"
          color={success ? "success" : "primary"}
          fullWidth
          type="submit"
          sx={{
            mt: 3,
            mb: 2,
            backgroundColor: "#e9a132",
            "&:hover": { backgroundColor: "#d8902e" },
          }}
          loading={loading}
          loadingPosition="start"
          startIcon={success ? <CheckCircleIcon /> : null}
          disabled={success}
        >
          {success ? "Success" : "Login"}
        </LoadingButton>

        <Grid
          container
          sx={{ display: "flex", flexDirection: "column", gap: "5px" }}
        >
          <Grid item xs>
            <Link
              href="/password-reset-options"
              variant="body2"
              style={{
                color: "#e9a132",
                textDecoration: "none",
              }}
              onMouseOver={(e) =>
                (e.target.style.textDecoration = "underline purple")
              }
              onMouseOut={(e) => (e.target.style.textDecoration = "none")}
            >
              Forgot password?
            </Link>
          </Grid>
          <Grid item>
            <Link
              href="/register"
              variant="body2"
              style={{ color: "#e9a132", textDecoration: "none" }}
              onMouseOver={(e) =>
                (e.target.style.textDecoration = "underline purple")
              }
              onMouseOut={(e) => (e.target.style.textDecoration = "none")}
            >
              {"Don't have an account? Sign Up"}
            </Link>
          </Grid>
        </Grid>
      </Box>

      {alert && (
        <Snackbar
          open={alert}
          autoHideDuration={6000}
          onClose={() => setAlert(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={() => setAlert(false)} severity="error">
            {alertContent}
          </Alert>
        </Snackbar>
      )}

      <Snackbar
        open={redirectMessage}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity="info">
          You are already logged in. Redirecting...
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Login;
